import { encodeAbiParameters, parseAbiParameters } from 'viem'
import { Call, isFailedCall } from '../../../utils/multicall'
import {
  decodeLensRows,
  getPositionLensFor,
  LENS_FAMILY,
  PositionLensAbi,
} from '../position-lens'

export const usesRiverLens = (chainId: string): boolean =>
  getPositionLensFor(chainId, LENS_FAMILY.RIVER) !== undefined

const isAddress = (v: unknown): v is string =>
  typeof v === 'string' && /^0x[0-9a-fA-F]{40}$/.test(v)

/**
 * ONE call instead of `3 per TroveManager + 1`.
 *
 * River troves are ADDRESS-keyed, so the lens probes `getTroveStatus` (one
 * storage read per manager) and only pays the four-word
 * `getEntireDebtAndColl` walk plus `surplusBalances` on managers that hold an
 * ACTIVE trove. The chain-wide StabilityPool deposit is always emitted as the
 * 32-byte header, because it exists independently of any trove.
 */
export const buildRiverLensCall = (
  chainId: string,
  cfg: { xapp?: string },
  markets: { troveManager?: string }[],
  account: string,
): Call[] => {
  const lens = getPositionLensFor(chainId, LENS_FAMILY.RIVER)
  if (!lens) return []
  // Fail closed to the walk rather than encode a payload with a hole in it —
  // `encodeAbiParameters` throws on a malformed address, which would take the
  // whole request down instead of this one lender.
  if (!isAddress(cfg.xapp) || markets.some((m) => !isAddress(m.troveManager))) {
    return []
  }
  const payload = encodeAbiParameters(
    parseAbiParameters('address[], address'),
    [markets.map((m) => m.troveManager) as any, cfg.xapp as any],
  )
  return [
    {
      address: lens,
      name: 'getUserData',
      params: [[{ family: LENS_FAMILY.RIVER, payload }], account],
      abi: PositionLensAbi as any,
    },
  ]
}

/**
 * Rebuild the flat `data` array the per-manager walk would have produced, so
 * the converter's cursor arithmetic is untouched on both paths.
 *
 * Blob layout (RiverLens.sol): `uint256 stabilityPoolDeposit` header, then
 * `uint16 count` rows of `uint16 index, 6 x uint256` — debt, coll, pending
 * debt reward, pending coll reward, status, surplus. No flags byte.
 *
 * A manager the lens did not emit is filled with ZEROS — the lens skips any
 * trove whose status != 1, and the converter builds a position only for
 * `status === 1 && (debt > 0 || coll > 0)`, so the zero fill converts to
 * exactly what the walk's real answer would have.
 *
 * Returns `undefined` when the blob is unusable, which the caller must treat
 * as an incomplete read and never as an empty position.
 */
export const rebuildRiverData = (
  blobs: unknown,
  marketCount: number,
): any[] | undefined => {
  if (isFailedCall(blobs) || !Array.isArray(blobs) || blobs.length !== 1) {
    return undefined
  }
  let decoded
  try {
    decoded = decodeLensRows(blobs[0] as string, 6, 32, 0)
  } catch {
    return undefined
  }

  let spDeposit = 0n
  for (const b of decoded.header) spDeposit = (spDeposit << 8n) | BigInt(b)

  const out: any[] = []
  for (let i = 0; i < marketCount; i++) {
    // getEntireDebtAndColl tuple (positional — the converter's `field()`
    // falls back to indices), status, surplus: the walk's own order.
    out.push([0n, 0n, 0n, 0n], 0n, 0n)
  }
  for (const r of decoded.rows) {
    if (r.index >= marketCount) return undefined
    const base = r.index * 3
    out[base] = [r.words[0], r.words[1], r.words[2], r.words[3]]
    out[base + 1] = r.words[4]
    out[base + 2] = r.words[5]
  }
  out.push(spDeposit)
  return out
}
